import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { useAuth } from "@/src/lib/AuthContext";
import { auth } from "@/src/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CATEGORIES, SHOP_TYPES, COUNTRIES, PRICING_PLANS, MALL_ZONES } from "@/src/constants";
import { toast } from "sonner";
import { Store, MapPin, Phone, MessageSquare, Loader2, Globe, Mail, Instagram, Facebook, ShieldCheck } from "lucide-react";
import { db as firestoreDb } from "@/src/lib/firebase";
import { TutorialGuide } from "@/src/components/TutorialGuide";

export function BecomeSellerPage() {
  const { user, profile, isAuthReady } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showTutorial, setShowTutorial] = useState(true);
  const [userShopsCount, setUserShopsCount] = useState(0);

  React.useEffect(() => {
    const checkAuthAndShops = async () => {
      if (!isAuthReady) return;
      
      if (user) {
        // Check if user has a plan. If not, redirect to pricing
        if (!profile?.subscription) {
          toast.info("Por favor, selecciona un plan para empezar");
          navigate("/pricing");
          return;
        }

        // Check how many shops the user already has
        try {
          const q = query(collection(firestoreDb, "shops"), where("ownerUid", "==", user.uid));
          const snapshot = await getDocs(q);
          setUserShopsCount(snapshot.size);
          
          const planLimit = PRICING_PLANS.find(p => p.id === profile.subscription?.tier)?.limits.maxShops || 0;
          if (snapshot.size >= planLimit) {
            toast.error("Has alcanzado el límite de tiendas de tu plan actual");
          }
        } catch (error) {
          console.error("Error checking user shops:", error);
        }
      }
      
      setChecking(false);
    };
    checkAuthAndShops();
  }, [isAuthReady, user, profile, navigate]);

  const [formData, setFormData] = useState({
    shopName: "",
    description: "",
    type: "products",
    city: "",
    country: "Colombia",
    whatsapp: "",
    website: "",
    email: "",
    instagram: "",
    facebook: "",
    categories: [] as string[],
    mallZone: "otros",
  });

  if (!isAuthReady || checking) {
    return (
      <div className="container py-24 text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">Verificando estado de tu tienda...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-24 text-center">
        <h2 className="text-2xl font-bold mb-4">Debes iniciar sesión para ser vendedor</h2>
        <Button onClick={() => navigate("/login")}>Ir a Iniciar Sesión</Button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.categories.length === 0) {
      toast.error("Selecciona al menos una categoría");
      return;
    }

    setLoading(true);
    try {
      const planLimit = PRICING_PLANS.find(p => p.id === profile?.subscription?.tier)?.limits.maxShops || 0;
      if (userShopsCount >= planLimit) {
        toast.error("Debes mejorar tu plan para crear más tiendas");
        navigate("/pricing");
        return;
      }

      const shopId = `shop_${user.uid}_${Date.now()}`;
      const newShop = {
        id: shopId,
        ownerUid: user.uid,
        name: formData.shopName,
        description: formData.description,
        type: formData.type,
        categories: formData.categories,
        mallZone: formData.mallZone,
        location: {
          city: formData.city,
          country: formData.country,
        },
        whatsapp: formData.whatsapp,
        website: formData.website,
        email: formData.email,
        instagram: formData.instagram,
        facebook: formData.facebook,
        rating: 5.0,
        reviewCount: 0,
        featured: false,
        createdAt: new Date().toISOString(),
      };

      console.log("Attempting to create shop with ID:", shopId);
      // Create shop document
      await setDoc(doc(firestoreDb, "shops", shopId), newShop);
      console.log("Shop document created successfully");
      
      // Update user role to seller
      console.log("Updating user role to seller for UID:", user.uid);
      await setDoc(doc(firestoreDb, "users", user.uid), {
        role: "seller"
      }, { merge: true });
      console.log("User role updated successfully");

      toast.success("¡Felicidades! Tu tienda ha sido creada.");
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Detailed error creating shop:", error);
      toast.error("Error al crear la tienda: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (cat: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="container py-12 px-4 md:px-8 max-w-4xl">
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-primary/10 mb-6">
            <Store className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Convierte tu pasión en un negocio</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-4">
            Únete a cientos de emprendedores que ya están vendiendo sus productos y servicios en nuestra plataforma.
          </p>
          
          {profile?.subscription && (
            <div className="flex items-center justify-center gap-2 text-sm font-medium bg-white border rounded-full px-4 py-2 w-fit mx-auto shadow-sm">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <span>Plan Activo: <span className="text-primary uppercase">{profile.subscription.tier}</span></span>
              <span className="mx-2 text-muted-foreground">|</span>
              <span>Tiendas: {userShopsCount} / {PRICING_PLANS.find(p => p.id === profile.subscription?.tier)?.limits.maxShops}</span>
            </div>
          )}
        </div>

        {showTutorial && (
          <div className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Cómo crear tu tienda paso a paso</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowTutorial(false)}>
                Omitir tutorial
              </Button>
            </div>
            <TutorialGuide />
          </div>
        )}

        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit}>
        <div className="grid gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5 text-primary" />
                Información de la Tienda
              </CardTitle>
              <CardDescription>Estos datos serán públicos para tus clientes.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="shopName">Nombre de la Tienda</Label>
                <Input
                  id="shopName"
                  placeholder="Ej: Mi Dulce Rincón"
                  value={formData.shopName}
                  onChange={(e) => setFormData({ ...formData, shopName: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  placeholder="Cuéntanos qué hace especial a tu negocio..."
                  className="min-h-[100px]"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Tipo de Negocio</Label>
                  <div className="flex flex-wrap gap-2">
                    {SHOP_TYPES.map((type) => (
                      <Button
                        key={type.value}
                        type="button"
                        variant={formData.type === type.value ? "default" : "outline"}
                        onClick={() => setFormData({ ...formData, type: type.value as any })}
                        className="flex-1"
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="mallZone">Ubicación en el Mall (Zona)</Label>
                  <select
                    id="mallZone"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={formData.mallZone}
                    onChange={(e) => setFormData({ ...formData, mallZone: e.target.value })}
                    required
                  >
                    {MALL_ZONES.map(zone => (
                      <option key={zone.id} value={zone.id}>{zone.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Ubicación y Contacto
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    placeholder="Ej: Santiago"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="country">País</Label>
                  <select
                    id="country"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    required
                  >
                    <option value="" disabled>Selecciona un país</option>
                    {COUNTRIES.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="whatsapp" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" /> WhatsApp de contacto
                </Label>
                <Input
                  id="whatsapp"
                  placeholder="Ej: +56912345678"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email de contacto
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Ej: contacto@mitienda.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Sitio Web (opcional)
                </Label>
                <Input
                  id="website"
                  placeholder="Ej: https://www.mitienda.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="instagram" className="flex items-center gap-2">
                    <Instagram className="h-4 w-4" /> Instagram
                  </Label>
                  <Input
                    id="instagram"
                    placeholder="@usuario"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="facebook" className="flex items-center gap-2">
                    <Facebook className="h-4 w-4" /> Facebook
                  </Label>
                  <Input
                    id="facebook"
                    placeholder="Usuario o URL"
                    value={formData.facebook}
                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Categorías
              </CardTitle>
              <CardDescription>Selecciona las categorías que mejor describen tu negocio.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <Button
                    key={cat}
                    type="button"
                    variant={formData.categories.includes(cat) ? "default" : "outline"}
                    onClick={() => toggleCategory(cat)}
                    size="sm"
                    className="rounded-full"
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading ? "Creando tu tienda..." : "Lanzar mi Tienda Digital"}
          </Button>
        </div>
      </form>
    </div>
  </div>
</div>
  );
}
